import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EventNoteIcon from '@mui/icons-material/EventNote';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import paths from '@src/paths';

const PRIORITY_COLOR = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'error',
};

const WORK_DECISION_LABELS = {
  no_reply: 'No reply',
  reply_only: 'Reply only',
  reply_and_task: 'Reply + task',
  task_only: 'Task only',
  needs_human_review: 'Needs review',
};

function TaskCard({ task, statusOptions, onEdit, onStatusChange, onToggleComplete, onDelete }) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  const statusValue = task.status || 'backlog';
  const priorityLabel = useMemo(() => task.priority_display || task.priority, [task.priority, task.priority_display]);
  const hasSubtasks = Array.isArray(task.subtasks) && task.subtasks.length > 0;
  const sourceWorkDecisionLabel = WORK_DECISION_LABELS[task.source_email_work_decision] || '';

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit(task);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete(task);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={1.5} divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Checkbox
            checked={Boolean(task.is_completed)}
            onChange={(event) => onToggleComplete(task, event.target.checked)}
            size="small"
            sx={{ mt: -1, alignSelf: 'flex-start' }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {task.title}
            </Typography>
            {task.client_name && (
              <Typography variant="body2" color="text.secondary">
                {task.client_name}
              </Typography>
            )}
          </Box>
          <IconButton edge="end" size="small" onClick={handleMenuOpen}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            <MenuItem onClick={handleEdit}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </Menu>
        </Stack>

        <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
          {priorityLabel && (
            <Chip
              label={priorityLabel}
              size="small"
              color={PRIORITY_COLOR[task.priority] || 'default'}
              variant="filled"
            />
          )}
          {task.due_date && (
            <Chip
              size="small"
              icon={<EventNoteIcon fontSize="small" />}
              label={dayjs(task.due_date).format('MMM D, YYYY')}
              variant="outlined"
            />
          )}
          {task.source_email_thread_id ? (
            <Chip
              size="small"
              label={sourceWorkDecisionLabel ? `Inbox • ${sourceWorkDecisionLabel}` : 'From Inbox'}
              variant="outlined"
              color="secondary"
            />
          ) : null}
        </Stack>

        {task.source_email_thread_id ? (
          <Stack spacing={0.75}>
            <Typography variant="body2" color="text.secondary">
              {task.source_email_subject || 'Created from client inbox thread'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {task.source_email_router_category ? (
                <Chip
                  size="small"
                  label={task.source_email_router_category.replace(/_/g, ' ')}
                  variant="outlined"
                />
              ) : null}
              <Button
                size="small"
                component={RouterLink}
                to={`${paths.clientInbox}?thread=${task.source_email_thread_id}`}
                variant="text"
                sx={{ px: 0 }}
              >
                Open source inbox thread
              </Button>
            </Stack>
          </Stack>
        ) : null}

        <TextField
          select
          size="small"
          label="Status"
          value={statusValue}
          onChange={(event) => onStatusChange(task, event.target.value)}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {hasSubtasks && (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Subtasks</Typography>
            <Stack spacing={1}>
              {task.subtasks.map((subtask) => (
                <Paper variant="outlined" key={subtask.id} sx={{ p: 1.5, borderStyle: 'dashed' }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Checkbox
                        checked={Boolean(subtask.is_completed)}
                        onChange={(event) => onToggleComplete(subtask, event.target.checked)}
                        size="small"
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {subtask.title}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {subtask.priority_display && (
                            <Chip
                              label={subtask.priority_display}
                              size="small"
                              color={PRIORITY_COLOR[subtask.priority] || 'default'}
                              variant="outlined"
                            />
                          )}
                          {subtask.due_date && (
                            <Chip
                              size="small"
                              icon={<EventNoteIcon fontSize="small" />}
                              label={dayjs(subtask.due_date).format('MMM D, YYYY')}
                              variant="outlined"
                            />
                          )}
                          {subtask.assignee_name && (
                            <Chip size="small" label={subtask.assignee_name} variant="filled" />
                          )}
                        </Stack>
                      </Box>
                      <Tooltip title="Edit subtask">
                        <IconButton size="small" onClick={() => onEdit(subtask)}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete subtask">
                        <IconButton size="small" onClick={() => onDelete(subtask)} sx={{ color: 'error.main' }}>
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <TextField
                      select
                      size="small"
                      label="Status"
                      value={subtask.status || 'backlog'}
                      onChange={(event) => onStatusChange(subtask, event.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

TaskCard.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    client_name: PropTypes.string,
    assignee_name: PropTypes.string,
    due_date: PropTypes.string,
    priority: PropTypes.string,
    priority_display: PropTypes.string,
    status: PropTypes.string,
    notes: PropTypes.string,
    is_completed: PropTypes.bool,
    parent: PropTypes.number,
    source_email_thread_id: PropTypes.number,
    source_email_subject: PropTypes.string,
    source_email_work_decision: PropTypes.string,
    source_email_router_category: PropTypes.string,
    subtasks: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onToggleComplete: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default TaskCard;
